/**
 * Tenant-scoped opaque analytics anonymous key (plan P1 — mirror consentStorage).
 */
import {
  analyticsAnonymousStorageKey,
  generateAnalyticsAnonymousKey,
} from 'pi-kiosk-shared/analyticsAnonymousIdentity';

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getDefaultStorage(): StorageLike | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export interface GetOrCreateAnonymousKeyOptions {
  /** Must be true — server still requires latch / DB consent before persisting identity (FIX-18). */
  readonly consentGranted: boolean;
  readonly storage?: StorageLike | null;
}

/**
 * Returns the tenant-scoped opaque key from storage, creating one if missing.
 * Returns null when consent is not granted.
 */
export function getOrCreateAnonymousKey(
  tenantCode: string,
  options: GetOrCreateAnonymousKeyOptions,
): string | null {
  if (options.consentGranted !== true) {
    return null;
  }
  const storage = options.storage ?? getDefaultStorage();
  if (storage === null) {
    return null;
  }
  const key = analyticsAnonymousStorageKey(tenantCode);
  const existing = storage.getItem(key);
  if (existing !== null && existing.length > 0) {
    return existing;
  }
  const created = generateAnalyticsAnonymousKey();
  storage.setItem(key, created);
  return created;
}

export function clearAnonymousKey(
  tenantCode: string,
  storage: StorageLike | null = getDefaultStorage(),
): void {
  if (storage === null) {
    return;
  }
  storage.removeItem(analyticsAnonymousStorageKey(tenantCode));
}

export function readAnonymousKey(
  tenantCode: string,
  storage: StorageLike | null = getDefaultStorage(),
): string | null {
  if (storage === null) {
    return null;
  }
  return storage.getItem(analyticsAnonymousStorageKey(tenantCode));
}
